import { useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import InputBase from "@mui/material/InputBase";
import Typography from "@mui/material/Typography";
import { styled } from "../../../../lib/styled";
import { useAsyncHandler } from "../../../../hooks/useAsyncHandler";

export interface InlineEditableTextProps {
  value: string;
  /** Stores the new text. Throw to keep the field open and show why it failed. */
  onSave: (next: string) => Promise<void>;
  /** What the text is, for the field's accessible name and the hint: "Name", "Description". */
  label: string;
  /** Lets Enter add a line; the text is then saved with Ctrl/Cmd+Enter or by leaving the field. */
  multiline?: boolean;
  /** An empty text is not saved: the previous one stays. */
  required?: boolean;
  maxLength?: number;
  /** Shown, dimmed, while the text is empty, to invite writing one. */
  placeholder?: string;
  /** Without it the text is plain. */
  editable?: boolean;
  /** How the text reads when it is not being edited, when that differs from the text itself. */
  display?: (value: string) => string;
}

const Display = styled("span")<{ $editable: boolean }>(({ theme, $editable }) => ({
  display: "inline-block",
  maxWidth: "100%",
  whiteSpace: "pre-wrap",
  overflowWrap: "anywhere",
  borderRadius: theme.shape.borderRadius / 3,
  ...($editable && {
    cursor: "text",
    "&:hover, &:focus-visible": {
      outline: `1px dashed ${theme.palette.divider}`,
      outlineOffset: 2,
    },
  }),
}));

const Placeholder = styled("span")(({ theme }) => ({
  color: theme.palette.text.disabled,
}));

const Field = styled(InputBase)(({ theme }) => ({
  // The field takes the type of whatever it sits in, so a title stays a title while it is edited.
  font: "inherit",
  color: "inherit",
  lineHeight: "inherit",
  width: "100%",
  padding: 0,
  borderBottom: `1px solid ${theme.palette.primary.main}`,
  "& .MuiInputBase-input": { padding: 0, font: "inherit" },
}));

interface FieldEditorProps extends Omit<
  InlineEditableTextProps,
  "placeholder" | "editable" | "display"
> {
  onDone: () => void;
}

/** The field while the text is being edited. It is mounted only then, so each edit starts clean. */
function FieldEditor({
  value,
  onSave,
  label,
  multiline,
  required,
  maxLength,
  onDone,
}: FieldEditorProps) {
  const [draft, setDraft] = useState(value);
  // Set once the field is left on purpose (saved or cancelled), so the blur that follows is ignored.
  const closing = useRef(false);

  const save = useAsyncHandler(async () => {
    const next = draft.trim();
    if (next !== value && !(required && next === "")) await onSave(next);
    onDone();
  });

  function commit() {
    if (closing.current) return;
    closing.current = true;
    // A failed save keeps the field open for another try.
    void save.handle().then(() => {
      closing.current = false;
    });
  }

  function onKeyDown(event: KeyboardEvent) {
    if (event.key === "Escape") {
      event.preventDefault();
      closing.current = true;
      onDone();
    } else if (event.key === "Enter" && (!multiline || event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      commit();
    }
  }

  return (
    <>
      <Field
        autoFocus
        multiline={multiline}
        value={draft}
        disabled={save.loading}
        onChange={(event) => setDraft(event.target.value)}
        onFocus={(event) => event.target.select()}
        onBlur={commit}
        onKeyDown={onKeyDown}
        inputProps={{ "aria-label": label, maxLength }}
      />
      {save.error ? (
        <Typography variant="caption" color="error" role="alert" display="block">
          {save.error}
        </Typography>
      ) : null}
    </>
  );
}

/** Text that becomes a field on double-click, in place and in its own type, with no dialog.
 *  Enter or leaving the field saves; Escape puts the previous text back. */
export function InlineEditableText({
  value,
  onSave,
  label,
  multiline = false,
  required = false,
  maxLength,
  placeholder,
  editable = true,
  display,
}: InlineEditableTextProps) {
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <FieldEditor
        value={value}
        onSave={onSave}
        label={label}
        multiline={multiline}
        required={required}
        maxLength={maxLength}
        onDone={() => setEditing(false)}
      />
    );
  }

  return (
    <Display
      $editable={editable}
      tabIndex={editable ? 0 : undefined}
      title={editable ? `Double-click to edit the ${label.toLowerCase()}` : undefined}
      onDoubleClick={() => editable && setEditing(true)}
      onKeyDown={(event) => {
        if (editable && (event.key === "Enter" || event.key === "F2")) setEditing(true);
      }}
    >
      {(display ? display(value) : value) ||
        (editable && placeholder ? <Placeholder>{placeholder}</Placeholder> : null)}
    </Display>
  );
}
